import cn from 'classnames';
import PropTypes from 'prop-types';

import { RETAIL_ONLY_CART } from 'constants/cartTypes';
import { CONFIRM_CREDIT_CARD } from 'constants/checkoutButtons';
import { CHECKOUT_STEP_MAP, REVIEW_STEP } from 'constants/checkoutFlow';

import { SmallLoader } from 'components/Loader';
import ContinueButtonTop from 'components/checkout/caap/payment/paymentlist/ContinueButtonTop';
import ContinueButtonBottom from 'components/checkout/caap/payment/paymentlist/ContinueButtonBottom';
import PaymentListItemHeaderDesktop from 'components/checkout/caap/payment/paymentlist/PaymentListItemHeaderDesktop';
import SmallScreenOnly from 'components/checkout/caap/SmallScreenOnly';
import AddNewPaymentMobile from 'components/checkout/caap/payment/paymentlist/AddNewPaymentMobile';
import PromoSection from 'components/checkout/caap/payment/paymentlist/PromoSection';
import AddPaymentAndGiftCardDesktop from 'components/checkout/caap/payment/paymentlist/AddPaymentAndGiftCardDesktop';
import AddGiftCardMobile from 'components/checkout/caap/payment/paymentlist/AddGiftCardMobile';
import ConfirmCardButton from 'components/checkout/caap/payment/paymentlist/ConfirmCardButton';
import ConfirmCardError from 'components/checkout/caap/payment/paymentlist/ConfirmCardError';
import ReEnterCardText from 'components/checkout/caap/payment/paymentlist/ReEnterCardText';
import PaymentListItem from 'components/checkout/caap/payment/paymentlist/PaymentListItem';
import BackButton from 'components/checkout/caap/BackButton';
import ListItem from 'components/checkout/caap/ListItem';
import { isCreditCardExpired } from 'helpers/MyAccountUtils';
import styles from 'styles/components/checkout/caap/payment/paymentList.scss';
import {
  first as styleFirst,
  item as styleItem,
  itemBox as styleItemBox,
  itemContainer as styleItemContainer,
  selected as styleSelected
} from 'styles/components/checkout/caap/contentBox.scss';

const PaymentList = (componentParams, { testId }) => {
  const {
    canUseThisPayment,
    cartType,
    confirmCardMessage,
    constraintViolations,
    showCCIVRPayment,
    eligibleBalances,
    giftCards,
    isFetchingCheckoutData,
    newPayment,
    onCardValidate,
    onEditPaymentClick,
    onGiftCardChange,
    onGiftCardVerify, onPaymentSelected,
    onPaymentTypeFieldChange,
    onToggleGiftCardModalClick,
    onUpdateCreditCardClick,
    onUsePromoBalanceClick,
    onUseThisPaymentClick,
    pageHistory,
    payment,
    purchaseId,
    savedPayments,
    selectedPaymentId,
    selectedPaymentNeedsConfirmation = false,
    showAsRadio,
    usePromoBalance
  } = componentParams;

  if (!savedPayments) {
    return <SmallLoader />;
  }

  const sectionTitleStyles = cn({ [styles.titleWhenBackBtn]: pageHistory && pageHistory.includes('spc') });

  return (
    <div data-test-id={testId('paymentListContainer')} className={styles.paymentListContainer}>
      {
        pageHistory && pageHistory.includes('spc')
          ? <BackButton href={CHECKOUT_STEP_MAP[REVIEW_STEP]} />
          : null
      }

      <SmallScreenOnly>
        <h1 className={sectionTitleStyles}>Select a payment method</h1>
      </SmallScreenOnly>

      <ContinueButtonTop
        canUseThisPayment={canUseThisPayment}
        isFetchingCheckoutData={isFetchingCheckoutData}
        onUseThisPaymentClick={onUseThisPaymentClick}
        selectedPaymentNeedsConfirmation={selectedPaymentNeedsConfirmation}
      />

      <div>
        <PaymentListItemHeaderDesktop />

        {
          savedPayments.map((type, index) => {
            const {
              paymentInstrumentId,
              addCreditCardNumber,
              expirationMonth,
              expirationYear,
              fullName,
              paymentType
            } = type;

            const isExpired = isCreditCardExpired(type);
            const isChecked = selectedPaymentId === paymentInstrumentId;
            const errors = payment.errors || {};
            let errorList = [];

            Object.keys(errors).forEach(key => {
              if (errors[key]) {
                errorList = [...errorList, ...errors[key]];
              }
            });

            const confirmCardButtonId = `${CONFIRM_CREDIT_CARD}-${paymentInstrumentId}`;
            const itemBoxStyles = cn(
              styleItemBox,
              {
                [styleSelected]: paymentInstrumentId === selectedPaymentId,
                [styleFirst]: index === 0
              }
            );

            return (
              <div className={`${styles.paymentItemContainer} ${styleItemContainer}`} key={`pay-list-${index}`}>
                <div className={itemBoxStyles}>
                  <form method="post" onSubmit={e => onCardValidate(e, paymentInstrumentId)}>

                    <ReEnterCardText
                      isChecked={isChecked}
                      isExpired={isExpired}
                      selectedPaymentNeedsConfirmation={selectedPaymentNeedsConfirmation}
                    />

                    <ConfirmCardError
                      confirmCardMessage={confirmCardMessage}
                      isChecked={isChecked}
                      selectedPaymentNeedsConfirmation={selectedPaymentNeedsConfirmation}
                    />
                  </form>

                  <div className={styleItem} data-test-id={testId('paymentListItem')}>
                    <div className={styles.paymentItem}>
                      <ListItem
                        itemKey={paymentInstrumentId}
                        index={`savedPayments-${index}`}
                        key={`savedPayments-${index}`}
                        onChange={onPaymentSelected}
                        isChecked={isChecked}
                        isExpired={isExpired}
                        onEditItemClick={onEditPaymentClick}
                      >
                        <PaymentListItem
                          addCreditCardNumber={addCreditCardNumber}
                          showCCIVRPayment={showCCIVRPayment}
                          errors={errors}
                          errorList={errorList}
                          expirationMonth={expirationMonth}
                          expirationYear={expirationYear}
                          fullName={fullName}
                          isChecked={isChecked}
                          isExpired={isExpired}
                          isFetchingCheckoutData={isFetchingCheckoutData}
                          newPayment={newPayment}
                          onPaymentTypeFieldChange={onPaymentTypeFieldChange}
                          onUpdateCreditCardClick={onUpdateCreditCardClick}
                          paymentInstrumentId={paymentInstrumentId}
                          paymentType={paymentType}
                          selectedPaymentNeedsConfirmation={selectedPaymentNeedsConfirmation}
                          type={type}
                        />
                      </ListItem>
                    </div>
                  </div>

                  <ConfirmCardButton
                    confirmCardButtonId={confirmCardButtonId}
                    isChecked={isChecked}
                    isExpired={isExpired}
                    isFetchingCheckoutData={isFetchingCheckoutData}
                    onCardValidate={onCardValidate}
                    paymentInstrumentId={paymentInstrumentId}
                    selectedPaymentNeedsConfirmation={selectedPaymentNeedsConfirmation}
                    showCCIVRPayment={showCCIVRPayment}
                  />
                </div>
              </div>
            );
          })
        }

        <AddNewPaymentMobile purchaseId={purchaseId} />
      </div>

      <PromoSection
        balance={getPromoTotal(cartType, eligibleBalances)}
        cartType={cartType}
        eligibleBalances={eligibleBalances}
        onEditPaymentClick={onEditPaymentClick}
        onPaymentSelected={onPaymentSelected}
        onUsePromoBalanceClick={onUsePromoBalanceClick}
        selectedPaymentId={selectedPaymentId}
        showAsRadio={showAsRadio}
        usePromoBalance={usePromoBalance}
      />

      <AddPaymentAndGiftCardDesktop
        canUseThisPayment={canUseThisPayment}
        constraintViolations={constraintViolations}
        giftCards={giftCards}
        isFetchingCheckoutData={isFetchingCheckoutData}
        onGiftCardChange={onGiftCardChange}
        onGiftCardVerify={onGiftCardVerify}
        onToggleGiftCardModalClick={onToggleGiftCardModalClick}
        onUseThisPaymentClick={onUseThisPaymentClick}
        purchaseId={purchaseId}
      />

      <AddGiftCardMobile
        constraintViolations={constraintViolations}
        giftCards={giftCards}
        isFetchingCheckoutData={isFetchingCheckoutData}
        onGiftCardChange={onGiftCardChange}
        onGiftCardVerify={onGiftCardVerify}
        onToggleGiftCardModalClick={onToggleGiftCardModalClick}
      />

      <ContinueButtonBottom
        canUseThisPayment={canUseThisPayment}
        isFetchingCheckoutData={isFetchingCheckoutData}
        onUseThisPaymentClick={onUseThisPaymentClick}
        selectedPaymentId={selectedPaymentId}
        selectedPaymentNeedsConfirmation={selectedPaymentNeedsConfirmation}
        showAsRadio={showAsRadio}
      />

    </div>
  );
};

const getPromoTotal = (cartType, eligibleBalances) => {
  const { combinedDiscountBalance, couponBalance } = eligibleBalances;

  // if retail only, can use both promo and gc
  if (cartType === RETAIL_ONLY_CART) {
    return combinedDiscountBalance;
  } else {
    // otherwise, can only use promo
    return couponBalance;
  }
};

PaymentList.contextTypes = {
  testId: PropTypes.func
};

export default PaymentList;
